// Centralized case study metadata - single source of truth
// This data is extracted from the actual case study pages

export interface CaseStudyMetadata {
  slug: string
  title: string
  company: string
  category: string
  heroImage: string
  heroDescription: string
  overview: {
    description: string
    keyAchievements: string[]
    projectDetails: {
      role: string
      timeline: string
      team?: string
      projectType?: string
    }
  }
}

export const caseStudyMetadata: Record<string, CaseStudyMetadata> = {
  amira: {
    slug: 'amira',
    title: 'Amira',
    company: 'Silo AI',
    category: 'AI AGENT HARNESS',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/x4S7EPV4CN7QuEvhUbsnuF/mockuuups-macbook-pro-16-inch-mockup-on-a-textured-gray-surface-with-dramatic-lighting.jpg',
    heroDescription:
      'Designing an adaptive, human-guided copilot for the way teams already build',
    overview: {
      description:
        'Amira grew out of Context, a 2025 workflow intelligence concept centered on observing how teams actually work across design, research, and delivery. As Head of AI Design Strategy and Research at Silo AI, I evolved that thinking into a proprietary, portable harness for building and orchestrating agents while keeping expert judgment in the loop. Amira stands on its own and connects into the Silo backend when work needs stronger controls.',
      keyAchievements: [
        'Defined the Context Mirror and Coach model to observe workflow patterns before suggesting or automating change',
        'Designed the human-assisted Miro, Magic Patterns, Dovetail, Figma, Jira, Slack, and engineering iteration loop',
        'Created the Design Studio, model-routing UX, browser automation, and persistent project memory surfaces',
        'Designed the human-gated self-improvement loop (Dream Engine) with governance as the default',
        'Built on Next.js and Python with WebAssembly components for portable agents, orchestrated through Eko',
        'Framed Silo as the backend layer Amira connects into for the future of digital building',
      ],
      projectDetails: {
        role: 'Head of AI Design Strategy and Research',
        projectType: 'Proprietary AI agent harness',
        timeline: '2025 to 2026',
      },
    },
  },

  'case-western': {
    slug: 'case-western',
    title: 'Case Western Reserve University',
    company: 'Case Western Reserve University',
    category: 'UX/UI REDESIGN 2025',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/r6QmYhNhTgdAoSYhnocL3u/CWRU_COMPV.png',
    heroDescription:
      'Leading a comprehensive UX/UI redesign of the Faculty Information System',
    overview: {
      description:
        "I led a comprehensive two-month UX/UI redesign of Case Western Reserve University's Faculty Information System (FIS), the central tracking system for faculty academic activities. Through rapid qualitative research with 16 users, I identified critical pain points and developed an AI-powered solution that achieved 85%+ accuracy in parsing CVs and populating review data, reducing entry time from 30 minutes to seconds.",
      keyAchievements: [
        'Conducted 16 user interviews (8 staff, 8 faculty) across different colleges in a two-week sprint',
        'Identified four critical pain points through qualitative research and affinity diagramming',
        'Developed AI-powered proof of concept achieving 85%+ accuracy in CV parsing and data population',
        'Secured two-month pilot program with Amazon for real-world testing',
        'Delivered three core improvements: standardized terminology, enhanced visual hierarchy, and improved navigation',
        'Scheduled for full implementation in 2026, transforming annual review experience for all faculty',
      ],
      projectDetails: {
        role: 'Strategic Product Designer',
        projectType: 'UX/UI Redesign',
        timeline: '2 Months',
      },
    },
  },

  'us-bank': {
    slug: 'us-bank',
    title: 'US Bank Commercial Card',
    company: 'US Bank',
    category: 'FINANCIAL SERVICES',
    heroImage:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/cGTn1zWyBRLJq9z8XJ288f/usbthumb.png',
    heroDescription:
      'Transforming a 54-day manual corporate card application process into a streamlined digital platform',
    overview: {
      description:
        "In 2022, I joined U.S. Bank's Team Flash to refine a newly launched digital onboarding experience for corporate card applications. The challenge was to transform a 54-day manual process into a streamlined digital platform while addressing critical usability issues that emerged after launch.",
      keyAchievements: [
        'Reduced application processing time from 54 days to just 20 days',
        'Achieved 96% adoption rate across Corporate Payment Services',
        'Implemented collaborative workflow system enabling multiple stakeholders to complete assigned portions independently',
        'Reached 98% user satisfaction rate by October 2024',
      ],
      projectDetails: {
        role: 'Lead UX Designer',
        timeline: '2022-2023',
        team: 'Team Flash',
      },
    },
  },

  'cyber-security': {
    slug: 'cyber-security',
    title: 'Cyber Security Platform Redesign',
    company: 'Unisys Stealth',
    category: 'DIGITAL PROTECTION',
    heroImage:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/odCcwZqxVnrHSvVy5xeJrh/unisys_thumb.png',
    heroDescription:
      'Transforming complex cybersecurity applications into intuitive solutions',
    overview: {
      description:
        'Transformed complex cybersecurity micro segmentation and threat analysis applications into intuitive, user-centered solutions. Redesigned data visualization for security threats and simplified workflows for technical security professionals.',
      keyAchievements: [
        'Redesigned complex data visualization for security threats',
        'Simplified micro segmentation workflows',
        'Created intuitive interfaces for technical security professionals',
        'Balanced technical depth with usability',
      ],
      projectDetails: {
        role: 'Product Designer',
        timeline: '2021-2022',
      },
    },
  },

  'ai-comparison': {
    slug: 'ai-comparison',
    title: 'Watson vs Dialogflow',
    company: 'MoneyGram',
    category: 'AI COMPARISON',
    heroImage:
      'https://uploadthingy.s3.us-west-1.amazonaws.com/d2sojXsLucASkJnUpLu1Ks/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png',
    heroDescription:
      'Comprehensive analysis and implementation of conversational AI platforms',
    overview: {
      description:
        'Comprehensive analysis and implementation of conversational AI platforms, comparing IBM Watson and Google Dialogflow for customer service automation. Built working conversational AI demos and designed natural dialogue patterns.',
      keyAchievements: [
        'Evaluated and compared major AI platforms (Watson, Dialogflow, Microsoft, Amazon)',
        'Built working conversational AI demos ("Track Status", "Fee Lookup")',
        'Designed natural dialogue patterns and conversation flows',
        'Reduced call center volume through intelligent automation',
      ],
      projectDetails: {
        role: 'Conversational AI Designer',
        timeline: '2020-2021',
      },
    },
  },
}

// Helper function to get metadata by slug
export function getCaseStudyMetadata(slug: string): CaseStudyMetadata | null {
  return caseStudyMetadata[slug] || null
}

// Helper function to get thumbnail for chat cards
export function getCaseStudyThumbnail(slug: string): string {
  const metadata = caseStudyMetadata[slug]
  return metadata?.heroImage || '/placeholder.png'
}

// Helper function to get all case studies
export function getAllCaseStudies(): CaseStudyMetadata[] {
  return Object.values(caseStudyMetadata)
}
